import Cookies from 'js-cookie';

import PublicEnv from 'shared/utils/public-env';

export const DEFAULT_COOKIE_NAME = 'dutchie_ctx';
const COOKIE_NAME = PublicEnv.sessionContextCookieName ?? DEFAULT_COOKIE_NAME;
const COOKIE_EXPIRES_DAYS = 30;

export type SessionContext = {
  storeSlug?: string;
  storeName?: string;
  firstName?: string;
};

const KNOWN_KEYS: Array<keyof SessionContext> = ['storeSlug', 'storeName', 'firstName'];

// base64 via Buffer, not btoa/atob -- Node's own types flag btoa/atob as legacy string-based
// APIs kept only for web-platform compatibility. Buffer.from handles multi-byte UTF-8
// correctly on its own (storeName is merchant-controlled and routinely contains smart
// quotes, accents, etc.). Plain 'base64', not 'base64url': Next.js's bundled buffer polyfill
// (next/dist/compiled/buffer, what Buffer actually resolves to in this client bundle) throws
// `TypeError: Unknown encoding: base64url` -- it doesn't implement that variant. Plain base64's
// alphabet (+, /, =) is still Cookie-safe (RFC 6265 cookie-octet permits it, and js-cookie's
// default converter preserves those characters literally) and the companion Cloudflare Worker's
// base64UrlDecode (workers/session-context-api/src/session-context.ts) tolerates it fine: its
// -/_ substitution and re-padding are both no-ops on already-valid, already-padded base64.
function encode(context: SessionContext): string {
  return Buffer.from(JSON.stringify(context)).toString('base64');
}

// Strips any key not in KNOWN_KEYS and any value that isn't a string.
function sanitize(parsed: Record<string, unknown>): SessionContext {
  const result: SessionContext = {};
  KNOWN_KEYS.forEach((key) => {
    const value = parsed[key];
    if (typeof value === 'string') {
      result[key] = value;
    }
  });
  return result;
}

function decode(value: string): SessionContext | null {
  try {
    const parsed = JSON.parse(Buffer.from(value, 'base64').toString('utf-8'));
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }
    return sanitize(parsed as Record<string, unknown>);
  } catch {
    // Corrupt/foreign cookie value -- fail open with no context rather than throwing.
    return null;
  }
}

export function readSessionContextCookie(): SessionContext {
  const raw = Cookies.get(COOKIE_NAME);
  if (!raw) {
    return {};
  }
  return decode(raw) ?? {};
}

export function patchSessionContextCookie(patch: Partial<SessionContext>): void {
  const next = { ...readSessionContextCookie(), ...patch };
  Cookies.set(COOKIE_NAME, encode(next), { expires: COOKIE_EXPIRES_DAYS });
}
